import { useTrackingContext } from "#app/TrackingProvider.tsx";
import { ConnectorChannelIcon } from "#components/ui/ConnectorChannelIcon.tsx";
import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { useConnectIntegration } from "#features/conversations/hooks/useConnectIntegration.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Div, Flex } from "@hopper-ui/components";
import { DismissIcon } from "@hopper-ui/icons";
import { useCallback, useMemo, useState } from "react";
import "./SlackCallout.css";

const STORAGE_KEY = "slackMcpCallout.dismissed";
const SLACK_HEX_COLOR = "#611f69";

export const SlackCallout = () => {
    const [isDismissed, setIsDismissed] = useState(() =>
        localStorage.getItem(STORAGE_KEY) !== null
    );
    const { trackEvent } = useTracking();
    const connect = useConnectIntegration();
    const { data: { personalIntegrations } } = useGetChannelsDetailsSuspenseQuery();
    const { tShared } = useSharedTranslation();
    const { trackingAppIdentifier } = useTrackingContext();

    const isPersonalSlackConnected = useMemo(() => {
        return personalIntegrations.some(x => x.channelType === "Slack" && !x.isConnected);
    }, [personalIntegrations]);

    const onActionPress = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Slack Banner",
            Trigger: "Connect Apps",
            Type: "Slack MCP"
        });

        connect({ type: "integration", provider: "slack" });
    }, [trackEvent, connect]);

    const onDismissPress = useCallback(() => {
        const dismissedAt = new Date().toISOString();

        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Slack Banner",
            Trigger: "Dismiss",
            Type: "Slack MCP"
        });

        setIsDismissed(true);
        localStorage.setItem(STORAGE_KEY, dismissedAt);
    }, [trackEvent]);

    const bannerTitle = useMemo(() => {
        switch (trackingAppIdentifier) {
            case "wpm":
                return tShared("slackMcpCallout.wpmTitle");
            case "wov":
                return tShared("slackMcpCallout.wovTitle");
            default:
                return tShared("slackMcpCallout.title");
        }
    }, [tShared, trackingAppIdentifier]);

    const bannerDescription = useMemo(() => {
        switch (trackingAppIdentifier) {
            case "wpm":
                return tShared("slackMcpCallout.wpmDescription");
            case "wov":
                return tShared("slackMcpCallout.wovDescription");
            default:
                return tShared("slackMcpCallout.description");
        }
    }, [tShared, trackingAppIdentifier]);

    if (isDismissed || !isPersonalSlackConnected) {
        return null;
    }

    return (
        <Div
            UNSAFE_maxWidth="var(--wai-page-max-width)"
            marginX="auto"
            width="100%"
            marginBottom="inline-md"
            position="relative"
            className="wai-connect-banner-container"
        >
            <Flex
                gap="inline-sm"
                padding="inset-md"
                borderRadius="pill"
                UNSAFE_backgroundColor={SLACK_HEX_COLOR}
                className="wai-connect-banner-wrapper"
            >
                <Flex
                    paddingLeft="inset-sm"
                    paddingY="inset-sm"
                    shrink={0}
                >
                    <ConnectorChannelIcon
                        isAlt
                        UNSAFE_width="1.75rem"
                        UNSAFE_height="1.75rem"
                        type="Slack"
                    />
                </Flex>
                <Flex
                    className="wai-connect-banner-content"
                    gap="inline-md"
                    grow={1}
                    justifyContent="space-between"
                >
                    <Div>
                        <Div
                            fontSize="body-md"
                            fontWeight="body-md-bold"
                            color="core_samoyed"
                        >
                            {bannerTitle}
                        </Div>
                        <Div
                            fontSize="body-sm"
                            color="core_samoyed"
                        >
                            {bannerDescription}
                        </Div>
                    </Div>
                    <Button
                        size="sm"
                        variant="primary"
                        onPress={onActionPress}
                        flexShrink={0}
                        backgroundColor="core_samoyed"
                        backgroundColorHover="transparent"
                        border="core_samoyed"
                        UNSAFE_color={SLACK_HEX_COLOR}
                        UNSAFE_colorHover="core_samoyed"
                    >
                        {tShared("slackMcpCallout.action")}
                    </Button>
                </Flex>
                <Button
                    size="xs"
                    variant="ghost-secondary"
                    onPress={onDismissPress}
                    flexShrink={0}
                    marginLeft="auto"
                    color="core_samoyed"
                    UNSAFE_colorHover={SLACK_HEX_COLOR}
                    backgroundColorHover="core_samoyed"
                >
                    <DismissIcon size="sm" />
                </Button>
            </Flex>
        </Div>
    );
};
